import { Location } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Injectable, Input } from '@angular/core';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { FirebaseFunctions } from '@capacitor-firebase/functions';
import { Device } from '@capacitor/device';
import { AlertController, IonicSafeString, LoadingController, ModalController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { BehaviorSubject, from, Observable, of, Subscription } from 'rxjs';
import { concatMap, first, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { CLEAR_ACTIONS } from './../../store/clear-session.actions';

import { environment } from '../../../environments/environment';
import { AppSettings } from '../../app.settings';
import { MenuItem } from '../../private/shared/models/menu-item';
import {
  setUserInfoApp,
  statusLifesModal,
  statusModal
} from '../../store/actions';
import { AppState } from '../../store/app-reducers';
import { AZURE_CA_URLS } from '../enums/HttpStatusCodes';
import { IIntervalSave } from '../enums/UpdateInfo';
import { IProductItemsList } from '../models/Analytics';
import { ApplicationUser, ISuccesfull, IUserData, Users } from '../models/ApplicationUser';
import { DATA_STORAGE_KEYS, MenuTabs, NativeStorageNamesV2 } from '../models/Constants';
import { IBottleLoading } from '../models/Error';
import { ILegacyVersions } from '../models/Legales';
import { IRequestEndpoint } from '../models/request.model';
import { IPromosEcommerce, ISaveGetmStorage } from './../../private/shared/models/productos';
import { IConfigDataState } from './../../store/reducers/data-user.reducer';
import { AnalyticsService } from './analytics.service';
import { FcmTokenService } from './fcm/fcm-token.service';
import { MonitoringService } from './monitoring/monitoring.service';
import { PerformanceService } from './performance/performance.service';
import { RequestService } from './request/request.service';
import { StorageData } from './utilities/storage-data.class';

@Injectable({
  providedIn: 'root'
})
export class SharedServiceService {

  @Input() LlaveConfiguracion = '';
  @Input() sendTransactionLimitEvent = false;
  @Input() limiteMinAdvertencia = 0;
  @Input() forceRefresh = false;

  public numeroTelefonico = new BehaviorSubject<number | null>(null)
  public dataTendero = new BehaviorSubject<ISuccesfull>({ ID_Bodega: 0, NUD: 0, Telefono: 0, Genero: 'no especificar' } as ISuccesfull);
  public urlWithTokenWA$ = new BehaviorSubject<string>('');
  public menuState = new BehaviorSubject<boolean>(false)
  public menuVisibleState = new BehaviorSubject<boolean>(false)
  public conexionState = new BehaviorSubject<boolean>(false)
  public isUrlWithTokenWA$ = new BehaviorSubject<boolean>(true);
  public renewToken$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public bodegaNud$ = new BehaviorSubject<IUserDataQuery>({ ID_Bodega: null, NUD: null });
  public menuTabState: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>(MenuTabs);
  public userDataInitialized$ = new BehaviorSubject<boolean>(false);

  private alert: any = null
  private openModal: HTMLIonModalElement | null = null
  private modalTyC: HTMLIonModalElement | null = null
  private bottleLoading: HTMLIonLoadingElement | null = null

  constructor(
    private performance: PerformanceService
    , private monitoringS: MonitoringService
    , private translate: TranslateService
    , private requestS: RequestService
    , private modalController: ModalController
    , private loadingCtrl: LoadingController
    , private socialSharing: SocialSharing
    , private readonly location: Location
    , public alertC: AlertController
    , private store: Store<AppState>
    , private nav: NavController
    , private readonly fcmTokenS: FcmTokenService
    , private analyticsS: AnalyticsService
  ) { }

  // Llamada a la CF para obtener los banners de la pantalla cliente
  public ContenidoClientesV1() {
    return {
      call: (data?: any) => {
        return from(FirebaseFunctions.callByName({
          name: 'ContenidoClientesV1',
          data: data || {}
        })).pipe(
          map((result: any) => result.data)
        );
      }
    };
  }

  // Metodos para el Local Storage: set, get y remove
  public setLocalStorage(value: string, contenido: any[]) {
    localStorage.setItem(value, JSON.stringify(contenido))
  }

  public getLocalStorage = <T>(value: string): T => JSON.parse(localStorage.getItem(value) || '{}');

  public removeLocalStorage(value: string) {
    localStorage.removeItem(value)
  }

  // Llamada a la CF para obtener datos del programa CDG
  public getProgramasTenderos = (api: string, data: any) => from(FirebaseFunctions.callByName({ name: api, data: data || {} })).pipe(map((result: any) => result.data));

  /**
   * Realiza la peticion ya incluida el ID_Bodega y NUD
   * @param api Nombre del endpoint a realizar la peticion
   * @param params Parameros extras necesarios para la peticion, puede
   * pasarle null
   */
  public executeFunctionDataUser = (api: string, params: any): Observable<any> => {
    return this.bodegaNud$.pipe(
      take(1),
      tap(() => this.performance.startTrace(api)),
      switchMap(storage => this.getProgramasTenderos(api, { ...storage, DeviceID: this.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID, ...params })),
      tap(() => this.performance.stopTrace(api)),
    )
  }

  /**
   * Executes a request by user.
   * 
   * @template T - The type of the request parameters.
   * @param {IRequestEndpoint} request - The request endpoint object.
   * @param {T} params - The request parameters.
   * @param {string} url - The base URL for the request (defaults to environment.urlApiV2).
   * @returns {Observable<any>} - An observable that emits the response from the request.
   */
  public executeRequestByUser<T>(request: IRequestEndpoint, params: T, url: string = environment.urlApiV2): Observable<any> {
    return this.bodegaNud$.pipe(
      take(1),
      tap(() => this.performance.startTrace(request.url)),
      switchMap(storage => this.requestS.requestHttp({
        method: request.method,
        url: `${url}${request.url}`,
        body: { ...storage, DeviceID: this.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID, ...params },
        init: { headers: { ...request.headers }, params: new HttpParams({ fromObject: { ...storage, DeviceID: this.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID, ...params } }), }
      })),
      tap(() => this.performance.stopTrace(request.url))
    );
  }

  public setDataUsertoStorage = async () => {
    const storage = await StorageData.getDataStorage(NativeStorageNamesV2.BodegaNud);
    if (storage) {
      this.bodegaNud$.next({ ID_Bodega: storage[0], NUD: storage[1], bodega: storage[0], nud: storage[1] });
      this.monitoringS.setUser({ idBodega: storage[0], nud: storage[1] });
    } else {
      this.bodegaNud$.next({ ID_Bodega: null, NUD: null });
      this.monitoringS.setUser(null)
    }
    const isWeb = (await Device.getInfo()).platform === 'web';
    const comesFromWatsApp = this.location.path().includes('token');
    await StorageData.setDataStorage(NativeStorageNamesV2.PlatformOrigin, isWeb && comesFromWatsApp ? 'wapp' : isWeb ? 'web' : 'app');
  }

  /**
   * Executes to request to services Azure Container Apps
   * 
   * @template T - The type of the request parameters.
   * @param {IRequestEndpoint} request - The request endpoint object.
   * @param {T} params - The request parameters.
   * @param {string} url - The base URL for the request (defaults to environment.urlApiV2).
   * @returns {Observable<any>} - An observable that emits the response from the request.
   */
  public executeRequestAzureCA<T>(request: IRequestEndpoint, params: T, url: string = environment.urlApiV2): Observable<any> {
    return this.bodegaNud$.pipe(
      take(1),
      tap(() => this.performance.startTrace(request.url)),
      switchMap(storage => this.requestS.requestHttp({
        method: request.method,
        url: `${url}${request.url}`,
        body: { data: { ...storage, DeviceID: this.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID, ...params } },
        init: { headers: { ...request.headers }, params: new HttpParams({ fromObject: { ...storage, DeviceID: this.getLocalStorage<ApplicationUser>(AppSettings.TOKEN_KEY).uniqueDeviceID, ...params } }), }
      })),
      tap(() => this.performance.stopTrace(request.url))
    );
  }

  /**
   * Obtiene la URL base de un servicio de Azure Container Apps
   * @param servCA - El nombre del servicio de Azure Container Apps
   * @returns La URL base del servicio
   */
  public getAzureCAUrl = (servCA: AZURE_CA_URLS) => environment.azureCA[servCA];

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public addCartItemsAnalitycsStorage = async (productsItemsList: IProductItemsList, promoId: any) => {
    const productsItemsListFormatted = productsItemsList.items.map(item => {
      const formattedItem: {
        item_id: string;
        item_list_id: string;
        item_list_name: string;
        quantity: number;
        promo_id: any;
        creative_name?: string;
        creative_slot?: string;
        item_category5?: string;
      } = {
        item_id: item.item_id,
        item_list_id: item.item_list_id || '',
        item_list_name: item.item_list_name || '',
        quantity: item.quantity,
        promo_id: promoId
      }
      if (promoId) {
        formattedItem.creative_name = item.creative_name;
        formattedItem.creative_slot = item.creative_slot;
        formattedItem.item_category5 = item.item_category5 || '';
      }
      return formattedItem;
    })
    const storage = await StorageData.getDataStorage(NativeStorageNamesV2.CartItemsAnalytics);
    if (storage) {
      const mergedItems = [
        ...storage,
        ...productsItemsListFormatted
      ].reduce((acc, item) => {
        const key = `${item.item_id}::${item.promo_id ?? 'null'}`;

        const existing = acc.get(key);
        if (existing) {
          acc.set(key, {
            ...existing,
            quantity: existing.quantity + item.quantity,
            item_list_id: item.item_list_id,
            item_list_name: item.item_list_name
          });
        } else {
          acc.set(key, { ...item });
        }

        return acc;
      }, new Map<string, typeof productsItemsListFormatted[number]>());

      await StorageData.setDataStorage(
        NativeStorageNamesV2.CartItemsAnalytics,
        Array.from(mergedItems.values())
      );
    } else {
      await StorageData.setDataStorage(NativeStorageNamesV2.CartItemsAnalytics, productsItemsListFormatted);
    }
  }

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public removeCartItemsAnalitycsStorage = async (
    productsItemsList: IProductItemsList,
    promoId: any
  ) => {
    const storage = await StorageData.getDataStorage(NativeStorageNamesV2.CartItemsAnalytics);
    if (!storage) return;

    const itemsToRemoveMap = new Map(
      productsItemsList.items.map(item => {
        const key = `${item.item_id}::${promoId ?? 'null'}`;
        return [key, item.quantity];
      })
    );
    const updatedStorage = storage
      .map((item: any) => {
        const key = `${item.item_id}::${item.promo_id ?? 'null'}`;
        const removeQty = itemsToRemoveMap.get(key);
        if (removeQty) {
          const newQty = item.quantity - removeQty;
          return newQty > 0
            ? { ...item, quantity: newQty }
            : null; // cantidad <= 0, se elimina
        }

        return item; // no hay que modificarlo
      })
      .filter(Boolean); // quita los null
    await StorageData.setDataStorage(NativeStorageNamesV2.CartItemsAnalytics, updatedStorage);
  };

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public getItemAnalitycsStorage = async (modelo: number | null | undefined, idProducto: number | null | undefined, promoId: any) => {
    const obtieneNativeStorage = await StorageData.getDataStorage(NativeStorageNamesV2.CartItemsAnalytics)

    const getDefaultItem = () => {
      let item_list_id = promoId ? "promociones" : "todos_los_productos";
      let item_list_name = promoId ? this.analyticsS.getListName('Promoción', 'Listado', 'Promociones') : this.analyticsS.getListName('Home', 'Listado', 'Todos los productos')
      const creative_slot = promoId ? "lista_promociones" : null;
      const creative_name = promoId ? "Lista promociones" : null
      if (modelo === 0) {
        item_list_id = "pedido_sugerido"
        item_list_name = this.analyticsS.getListName('Home', 'Carrusel', 'Pedido sugerido')
      }
      else if (modelo === 1) {
        item_list_id = "lo_mas_vendido_cerca_de_ti"
        item_list_name = this.analyticsS.getListName('Home', 'Carrusel', 'Lo mas vendido')
      }
      else if (modelo === 4) {
        item_list_id = "especiales_del_mes"
        item_list_name = this.analyticsS.getListName('Home', 'Carrusel', 'Especiales del mes')
      }

      return {
        item_list_id,
        item_list_name,
        creative_slot,
        creative_name,
        item_category5: ''
      };
    };

    if (!obtieneNativeStorage) {
      return getDefaultItem();
    }

    if (promoId != null) {
      const item = obtieneNativeStorage.find((item: any) => item.promo_id == promoId);
      return item || getDefaultItem();
    } else {
      const item = obtieneNativeStorage.find((item: any) => item.item_id == idProducto && item.promo_id == null);
      return item || getDefaultItem();
    }

  }

  public getStorage = <T>(storage: ISaveStorage<T>, keyStorage: string, interval: IIntervalSave, http: string, endpoint: IRequestEndpoint | string, params: any, url?: string): Observable<IGenericResp<T>> => {
    const date = moment().utc(false).subtract(6, 'hours');
    const service: any = this;

    if (this.forceRefresh) {
      return service[http](endpoint, params)
        .pipe(switchMap((resp: IGenericResp<T>) => this.saveDataStorage(keyStorage, resp, date)));
    }

    if (storage && !(typeof storage.data === "object" && Object.keys(storage.data || {}).length === 0)) {
      storage.fecha = storage.fecha.length < 9 ? moment(storage.fecha, "YYYYMMDD").utc(false).subtract(6, 'hours').toISOString() : storage.fecha;

      if (date.diff(moment(storage.fecha).utc(false), interval.typeTime) < interval.time) {
        const respData: IGenericResp<T> = {
          success: true,
          status: 'resp/valid-datos',
          message: 'Se obtuvieron los datos correctamente del Storage',
          data: storage.data
        };
        const dispatch_schedule$ = this.store.select('Configuraciones')
          .pipe(
            skipWhile(x => !x.success && !x.failed),
            first(),
            concatMap<IConfigDataState, Observable<IGenericResp<T>>>(config => {
              const time = config[this.LlaveConfiguracion];
              const utc = Math.abs(moment().utcOffset() / 60);
              const dispatch = moment(time, 'HH:mm').utc(false).subtract(utc, 'hours');
              const dispatch5 = moment(dispatch).subtract(5, 'minutes');

              if (date.isAfter(dispatch5) && moment(storage.fecha).utc(false).isBefore(dispatch5)) {
                return service[http](endpoint, params, url)
                  .pipe(switchMap((resp: IGenericResp<T>) => this.saveDataStorage(keyStorage, resp, date))) as Observable<IGenericResp<T>>;
              }
              return of(respData);
            }),
          );
        return of(respData)
          .pipe(
            switchMap(data => dispatch_schedule$.pipe(
              map(overtime => overtime.message === data.message ? data : overtime)
            )),
          );
      } else {
        return service[http](endpoint, params, url)
          .pipe(switchMap((resp: IGenericResp<T>) => this.saveDataStorage(keyStorage, resp, date)));
      }
    } else {
      return service[http](endpoint, params, url)
        .pipe(switchMap((resp: IGenericResp<T>) => this.saveDataStorage(keyStorage, resp, date)));
    }
  }

  public getGetmStorage = <T>(storage: ISaveGetmStorage<T>, keyStorage: string, http: string, endpoint: IRequestEndpoint | string, params: any): Observable<IGenericResp<T>> => {
    const date = moment().utc(false).subtract(6, 'hours');
    const service: any = this;

    if (this.forceRefresh) {
      return service[http](endpoint, params)
        .pipe(switchMap((resp: IGenericResp<T>) => this.saveOrNot(resp, keyStorage, date)));
    }

    if (storage && !(typeof storage.data === "object" && Object.keys(storage.data || {}).length === 0)) {
      storage.fecha = storage.fecha.length < 9 ? moment(storage.fecha, "YYYYMMDDHHmmss").utc(false).subtract(6, 'hours').toISOString() : storage.fecha;

      let isExpired = true;
      const ttl_segundos = storage?.ttl_segundos;
      if (ttl_segundos) {
        const diffInSeconds = date.diff(moment(storage.fecha).utc(false), 'seconds');
        isExpired = diffInSeconds >= ttl_segundos;
      }

      if (!isExpired) {
        const respData: IGenericResp<T> = {
          success: true,
          status: 'resp/valid-datos',
          message: 'Se obtuvieron los datos correctamente del Storage',
          data: storage.data
        };
        const dispatch_schedule$ = this.store.select('Configuraciones')
          .pipe(
            skipWhile(x => !x.success && !x.failed),
            first(),
            concatMap<IConfigDataState, Observable<IGenericResp<T>>>(config => {
              const time = config[this.LlaveConfiguracion];
              const utc = Math.abs(moment().utcOffset() / 60);
              const dispatch = moment(time, 'HH:mm').utc(false).subtract(utc, 'hours');
              const dispatch5 = moment(dispatch).subtract(5, 'minutes');

              if (date.isAfter(dispatch5) && moment(storage.fecha).utc(false).isBefore(dispatch5)) {
                return service[http](endpoint, params)
                  .pipe(switchMap((resp: IGenericResp<T>) => this.saveOrNot(resp, keyStorage, date))) as Observable<IGenericResp<T>>;
              }
              return of(respData);
            }),
          );
        return of(respData)
          .pipe(
            switchMap(data => dispatch_schedule$.pipe(
              map(overtime => overtime.message === data.message ? data : overtime)
            )),
          );
      } else {
        return service[http](endpoint, params)
          .pipe(switchMap((resp: IGenericResp<T>) => this.saveOrNot(resp, keyStorage, date)));
      }
    } else {
      return service[http](endpoint, params)
        .pipe(switchMap((resp: IGenericResp<T>) => this.saveOrNot(resp, keyStorage, date)));
    }
  }

  private saveOrNot = <T>(resp: IGenericResp<T>, keyStorage: string, date: moment.Moment) => {
    const dataObj = resp?.data as any;
    const errors = dataObj?._errors || (resp as any)?._errors;
    const hasErrors = (errors && typeof errors === 'object' && Object.keys(errors).length > 0) || !resp.hasOwnProperty('data');
    if (!hasErrors) {
      return this.saveDataStorage(keyStorage, resp, date);
    } else {
      return of(resp);
    }
  };

  private saveDataStorage = <T>(keyStorage: string, dataNew: IGenericResp<T>, date: moment.Moment): Observable<IGenericResp<T>> => {
    StorageData.setDataStorage(keyStorage, { data: dataNew.data, fecha: date.toISOString(), ttl_segundos: dataNew?.data?.['ttl_segundos'] ?? null });
    return of(dataNew);
  }

  public clearData = async (navigate: string) => {
    CLEAR_ACTIONS().forEach(action => this.store.dispatch(action));

    await StorageData.removeDataStorageMultiple(DATA_STORAGE_KEYS);
    const userData: IUserData = {
      usr_genero: '',
      usr_edad: '',
      usr_region: '',
      usr_id: '',
      usr_bodega: 0,
      usr_programa: '',
      usr_nivel: '',
      usr_territorio: '',
      usr_ruta: '',
      usr_canal: '',
      usr_idTendero: 0,
      usr_nud: 0,
      usr_negocio: ''
    };
    this.manageSessionStorageProperties(userData, false);

    this.emitConexionState(false);
    this.menuTabState.next([]);
    await this.nav.navigateRoot([`/${navigate}`]);

    this.bodegaNud$.next({ ID_Bodega: null, NUD: null });
    await this.fcmTokenS.deleteToken();
  }

  // Metodo para redirigir al usuario segun su programa
  public navigateUserData = (menuTabs: MenuItem[]) => {
    const menu = menuTabs.filter(m => m.principal === 1)[0];
    this.nav.navigateRoot(menu.route || '', { animated: true, animationDirection: 'forward' });
  }

  public updatedUserData = async (resp: Users, dataStorage: number) => {
    await StorageData.setDataStorage(NativeStorageNamesV2.BodegaNud
      , [resp.ID_Bodega, resp.NUD, resp.ID_Programa, dataStorage, resp.ID_Tendero, resp.Menu]);
    await this.setDataUsertoStorage();

    this.menuTabState.next(resp.ArrayMenuTabs);
    this.userDataInitialized$.next(true);
    this.emitMenu(resp.Menu);
    this.emitMenuVisible(resp.Menu);
    this.navigateUserData(resp.ArrayMenuTabs);

    const userData: IUserData = {
      usr_genero: resp.Genero || '',
      usr_edad: String(resp.Edad),
      usr_region: String(resp.Region).toLocaleLowerCase(),
      usr_id: `${resp.ID_Bodega}|${resp.NUD}`,
      usr_bodega: resp.ID_Bodega,
      usr_programa: String(resp.Programa).toLocaleLowerCase(),
      usr_nivel: String(resp.Nivel).toLocaleLowerCase(),
      usr_territorio: String(resp.Territorio).toLocaleLowerCase(),
      usr_ruta: resp.Ruta,
      usr_canal: resp.Canal || '',
      usr_idTendero: resp.ID_Tendero,
      usr_nud: resp.NUD,
      usr_negocio: resp.Segmento || ''
    };
    await StorageData.setDataStorage(NativeStorageNamesV2.PerfilUsuario, userData);
    StorageData.setDataStorage(NativeStorageNamesV2.OverlaysModulos, resp.OverlaysModulos);
    this.manageSessionStorageProperties(userData, true);

    this.store.dispatch(setUserInfoApp({
      Responsable: resp.Responsable || ''
      , ID_Bodega: resp.ID_Bodega
      , NUD: resp.NUD
      , ID_Programa: resp.ID_Programa
      , Menu: resp.Menu
      , MenuMas: resp.ArrayMenu
      , MenuHeader: resp.ArrayHeader
      , Ejecucion: { ID_Tendero: resp.ID_Tendero, FechasEjecucion: resp.FechasEjecucion }
    }));

    this.emitConexionState(true);
    this.store.dispatch(statusLifesModal());
    this.store.dispatch(statusModal({ ID_Tendero: userData.usr_idTendero }));
  }

  public async openModalDisabled(mensajes: string, component: any) {
    this.openModal = await this.modalController.create({
      component
      , mode: 'md'
      , backdropDismiss: true
      , componentProps: { mensajesConfiguracion: mensajes }
    })

    await this.openModal.present()
    await this.openModal.onDidDismiss()
  }

  private manageSessionStorageProperties(sessionObjet: IUserData, isAdd: boolean) {
    for (const [key, value] of Object.entries(sessionObjet)) {
      if (isAdd) {
        window.sessionStorage.setItem(`${key}`, value);
      } else {
        window.sessionStorage.removeItem(`${key}`);
      }
    }
  }

  // TODO: Implementar refactor para evitar el uso de servicios
  public emitTelefono(count: number | null) {
    this.numeroTelefonico.next(count)
  }

  // TODO: Implementar refactor para evitar el uso de servicios
  public emitDataTendero(count: ISuccesfull) {
    this.dataTendero.next(count)
  }

  public emitMenu = (count: boolean) => this.menuState.next(count);

  public emitMenuVisible = (state: boolean) => this.menuVisibleState.next(state);

  public emitConexionState = (state: boolean) => this.conexionState.next(state);

  public async presentAlert(message: string, cssClass: string) {
    this.alert = await this.alertC.create({
      backdropDismiss: false,
      cssClass,
      message,
      mode: 'md',
      buttons: [
        {
          text: 'ACEPTAR',
          role: 'cancel',
          handler: () => this.alert.dismiss()
        }
      ]
    })

    await this.alert.present()
  }

  public presentAlertAccept = async (loading: IBottleLoading) => {
    this.alert = await this.alertC.create({
      animated: true,
      backdropDismiss: loading.showBackdrop,
      keyboardClose: false,
      translucent: true,
      mode: 'ios',
      cssClass: loading.cssClass,
      message: new IonicSafeString(loading.message).value,
      buttons: [
        {
          text: loading.spinner,
          handler: () => this.alert.dismiss(true)
        }
      ]
    })
    await this.alert.present();

    const { data } = await this.alert.onDidDismiss();
    return data;
  }

  public alertDismiss() {
    if (this.alert !== null) {
      this.alert.dismiss()
    }
  }

  public loadingBottle = async () => {
    this.loadingDismissActive();
    const bottle = this.translate.instant('Miscellaneous.BottleProccess');
    this.bottleLoading = await this.loadingCtrl.create(bottle);
    return await this.bottleLoading.present();
  }

  public loadingBottleDismiss = async () => {
    if (this.bottleLoading !== null) {
      await this.bottleLoading.dismiss(true);
    }
    this.bottleLoading = null;
    return Promise.resolve(true);
  }

  public loadingGetop = async () => await this.loadingCtrl.getTop();

  public loadingDismissActive = () => {
    const overlays = document.querySelectorAll('ion-loading');
    if (overlays.length > 0) {
      overlays.forEach((overlay: any) => overlay.dismiss());
    }
  }

  public modalLegales = async (component: any, tipo: string, boton?: string): Promise<boolean> => {
    this.modalTyC = await this.modalController.create({
      component,
      componentProps: {
        tipo,
        boton: boton || 'ACEPTAR'
      },
      cssClass: 'ion-modal-overlayPage'
    })

    await this.modalTyC.present()
    const { data } = await this.modalTyC.onDidDismiss()

    return data
  }

  public dismissModalLegales = async () => {
    if (this.modalTyC !== null) {
      await this.modalTyC.dismiss()
    }
  }

  public validateLegalChange = (legales: ILegacyVersions[]) => legales.some(x => x.Cambio);

  public urlSharing(url: string, callbackNext: any, callbakError?: any): Subscription {
    return from(this.socialSharing.share(undefined, undefined, undefined, url)).subscribe({
      next: callbackNext,
      error: callbakError
    })
  }

  public async showShare(
    optionsShare: { message?: string | undefined, subject?: string | undefined, file?: string | undefined | string[], url?: string | undefined }
  ): Promise<boolean> {
    let shared = false
    try {
      await this.socialSharing.share(optionsShare.message, optionsShare.subject, optionsShare.file, optionsShare.url)
    } catch (err) {
      shared = false
    }

    return shared
  }

  public async saveToAlbum(urFile: string): Promise<boolean> {
    let savedToAlbum = false
    try {
      await this.socialSharing.saveToPhotoAlbum(urFile)
      savedToAlbum = true
    } catch (err) {
      savedToAlbum = false
    }

    return savedToAlbum
  }

  public orderPromosSlide(originalList: IPromosEcommerce[], typeOrder: string) {
    let newPromosEcommerce: IPromosEcommerce[] = [];
    const promosSunday = originalList.filter(p => p.Sunday);
    const promosTypeOrder = originalList.filter(p => p.TipoPromocion === typeOrder && !p.Sunday && !p.Bonificacion);
    const promosNoTypeOrder = originalList.filter(p => p.TipoPromocion !== typeOrder && !p.Sunday && !p.Bonificacion);
    const promoBonificacionDinamic = originalList.filter(p => p.TipoPromocion === 'BONIFICACION' && !p.Sunday && p.Bonificacion);
    newPromosEcommerce = promosSunday.concat(promoBonificacionDinamic, promosTypeOrder, promosNoTypeOrder);
    return newPromosEcommerce;
  }

  /**
   * Para obtener únicamente el valor del área segura inferior (bottom) en iOS
   */
  public getIOSSafeAreaBottom = (): number => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-inset-bottom')
      .trim();

    // Convierte "34px" a 34. Si falla o está vacío, retorna 0
    return value ? parseFloat(value) : 0;
  }

}

interface IUserDataQuery {
  ID_Bodega: any;
  NUD: any;
  bodega?: number;
  nud?: number;
  redsocial?: number;
  ID_Conversacion?: string;
  ID_Chat?: number;
}

interface IGenericResp<T> {
  success: boolean;
  status: string;
  message: string;
  data: T;
}

interface ISaveStorage<T> {
  fecha: string;
  data: T;
}